"use client"

import { cn } from "@rarible_int/anaconda_ui_components"
import { wrap } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  preloadGachaRevealWhoosh,
  setGachaRevealWhooshMuted,
  waitForGachaRevealWhoosh,
} from "./gacha-reveal-audio"
import type { GachaRevealCardPayload } from "./gacha-reveal-card"
import { GACHA_REVEAL_FADE_MS } from "./gacha-reveal-handoff"
import {
  preloadGachaRevealImages,
  preloadGachaRevealVideos,
} from "./gacha-reveal-media"
import { GachaRevealMuteButton } from "./gacha-reveal-mute-button"
import { GachaWatchMultiReveal } from "./gacha-watch-multi-reveal-presentation"
import {
  createWatchRevealPlan,
  getWatchRevealPreloadSources,
} from "./gacha-watch-reveal"
import { GachaWatchReveal } from "./gacha-watch-reveal-presentation"
import { useGachaRevealMute } from "./use-gacha-reveal-mute"

type GachaRevealOverlayProps = {
  open: boolean
  /** Mounts the iframe invisibly so the reveal runtime can initialize early. */
  preload?: boolean
  /** Keep EVM playback hidden until its runtime, images, videos and audio are ready. */
  waitForMedia?: boolean
  resultImages?: readonly string[]
  onStart?: () => void
  /** Increments on every buy so each reveal restarts from the beginning. */
  playId: number
  card: GachaRevealCardPayload | null
  /** When true, play pack carousel + bloom only, then hand off to the result dialog. */
  skipCardReveal?: boolean
  /** Selected Gacha Machine. Watch Machines use the Watch-specific reveal. */
  machineCode?: string
  /** Authoritative top-level Pack Open rarity, used only by the Watch reveal. */
  openedPackRarity?: string
  /** Number of Issued Packs opened in this reveal. */
  packCount?: number
  /** Card showcase fade started; open dialog and begin dialog fade-in. */
  onFade: () => void
  onComplete: () => void
}

const EMBED_PATH = "/gacha-reveal/embed.html"
const EMBED_BUILD = "20261013-media-ready"
const MEDIA_PRELOAD_TIMEOUT_MS = 15_000
const EMPTY_RESULT_IMAGES: readonly string[] = []
const START_RETRY_INTERVAL_MS = 250
const START_RETRY_TIMEOUT_MS = 6000
const SINGLE_PACK_REVEAL_FADE_MS = 4850
const SINGLE_PACK_REVEAL_MS = 5400
const MULTI_PACK_REVEAL_FADE_MS = 2050
const MULTI_PACK_REVEAL_MS = 2500
const REVEAL_FADE_GRACE_MS = 250
const REVEAL_COMPLETE_GRACE_MS = 900

const GachaRevealOverlayBase = ({
  open,
  preload = false,
  waitForMedia = false,
  resultImages = EMPTY_RESULT_IMAGES,
  onStart,
  playId,
  card,
  skipCardReveal = false,
  machineCode,
  openedPackRarity,
  packCount = 1,
  onFade,
  onComplete,
}: GachaRevealOverlayProps) => {
  const watchRevealPlan = createWatchRevealPlan({
    machineCode,
    packCount,
    rarity: openedPackRarity,
  })
  const usesWatchReveal = watchRevealPlan.kind !== "generic"
  const watchRevealPreloadSources = getWatchRevealPreloadSources(machineCode)
  const { muted, toggleMuted } = useGachaRevealMute()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [mounted, setMounted] = useState(false)
  const [iframeSession, setIframeSession] = useState<number | null>(null)
  const [isFading, setIsFading] = useState(false)
  const [fallbackStartPlayId, setFallbackStartPlayId] = useState(0)
  const iframeReadyRef = useRef(false)
  const pendingCardRef = useRef<GachaRevealCardPayload | null>(null)
  const startedPlayIdRef = useRef(0)
  const startSentRef = useRef(false)
  const fadedPlayIdRef = useRef(0)
  const completedPlayIdRef = useRef(0)
  const mutedRef = useRef(muted)
  const shouldRender = open || preload
  const [localMediaReadyPlayId, setLocalMediaReadyPlayId] = useState(0)
  const [iframeMediaReadyPlayId, setIframeMediaReadyPlayId] = useState(0)
  const playbackOpen =
    open &&
    (!waitForMedia ||
      (localMediaReadyPlayId === playId &&
        (usesWatchReveal || iframeMediaReadyPlayId === playId)))
  const onStartRef = useRef(onStart)
  onStartRef.current = onStart
  const mediaFailureRef = useRef(() => {})
  mediaFailureRef.current = () => {
    if (completedPlayIdRef.current === playId) return
    completedPlayIdRef.current = playId
    triggerFade()
    onComplete()
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: wrap preserves context; each reveal owns its media loading lifetime.
  useEffect(
    wrap(() => {
      if (!open || !waitForMedia || !card) return
      const controller = new AbortController()
      const signal = AbortSignal.any([
        controller.signal,
        AbortSignal.timeout(MEDIA_PRELOAD_TIMEOUT_MS),
      ])
      setLocalMediaReadyPlayId(0)
      void wrap(
        Promise.all([
          preloadGachaRevealImages(
            [card.image, card.imageBack, ...resultImages],
            signal,
          ),
          preloadGachaRevealVideos(
            usesWatchReveal ? getWatchRevealPreloadSources(machineCode) : [],
            signal,
          ),
          waitForGachaRevealWhoosh(signal),
        ]),
      ).then(
        wrap(() => {
          if (!controller.signal.aborted) setLocalMediaReadyPlayId(playId)
        }),
        wrap(() => {
          if (!controller.signal.aborted) mediaFailureRef.current()
        }),
      )
      return () => controller.abort()
    }),
    [
      card,
      machineCode,
      open,
      playId,
      resultImages,
      usesWatchReveal,
      waitForMedia,
    ],
  )

  useEffect(() => {
    if (!open || !waitForMedia || playbackOpen || playId <= 0) return
    const timeout = window.setTimeout(
      () => mediaFailureRef.current(),
      MEDIA_PRELOAD_TIMEOUT_MS,
    )
    return () => window.clearTimeout(timeout)
  }, [open, playbackOpen, playId, waitForMedia])

  useEffect(() => {
    if (playbackOpen && playId > 0) onStartRef.current?.()
  }, [playbackOpen, playId])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (card) pendingCardRef.current = card
  }, [card])

  useEffect(() => {
    mutedRef.current = muted
  }, [muted])

  const postMuted = useCallback((isMuted: boolean) => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: "GACHA_REVEAL_SET_MUTED", muted: isMuted },
      "*",
    )
  }, [])

  const postStart = useCallback(
    (
      payload: GachaRevealCardPayload,
      revealPlayId: number,
      isMuted: boolean,
      skipReveal: boolean,
    ) => {
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: "GACHA_REVEAL_START",
          playId: revealPlayId,
          payload,
          muted: isMuted,
          skipCardReveal: skipReveal,
        },
        "*",
      )
    },
    [],
  )

  const requestReady = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: "GACHA_REVEAL_PING" },
      "*",
    )
  }, [])

  const tryStartPlayback = useCallback(() => {
    if (usesWatchReveal || !playbackOpen) return

    const payload = pendingCardRef.current
    if (!payload || playId <= 0 || startedPlayIdRef.current !== playId) return
    if (startSentRef.current) return

    startSentRef.current = true
    postStart(payload, playId, mutedRef.current, skipCardReveal)
    if (!skipCardReveal || iframeReadyRef.current) {
      setFallbackStartPlayId(playId)
    }
  }, [playbackOpen, playId, postStart, skipCardReveal, usesWatchReveal])

  const handleMuteToggle = useCallback(() => {
    setGachaRevealWhooshMuted(!muted)
    toggleMuted()
  }, [muted, toggleMuted])

  const triggerFade = useCallback(() => {
    if (fadedPlayIdRef.current === playId) return

    fadedPlayIdRef.current = playId
    setIsFading(true)
    onFade()
  }, [onFade, playId])

  useEffect(() => {
    if (!shouldRender) return
    preloadGachaRevealWhoosh()
    postMuted(muted)
  }, [muted, postMuted, shouldRender])

  useEffect(() => {
    if (!shouldRender) {
      iframeReadyRef.current = false
      pendingCardRef.current = null
      startedPlayIdRef.current = 0
      startSentRef.current = false
      fadedPlayIdRef.current = 0
      completedPlayIdRef.current = 0
      setIsFading(false)
      setIframeSession(null)
      setFallbackStartPlayId(0)
      return
    }

    setIframeSession((session) => (session ?? 0) + 1)
  }, [shouldRender])

  useEffect(() => {
    if (!open) {
      setLocalMediaReadyPlayId(0)
      setIframeMediaReadyPlayId(0)
      pendingCardRef.current = null
      startedPlayIdRef.current = 0
      startSentRef.current = false
      fadedPlayIdRef.current = 0
      completedPlayIdRef.current = 0
      setIsFading(false)
      setFallbackStartPlayId(0)
      return
    }

    startSentRef.current = false
    fadedPlayIdRef.current = 0
    completedPlayIdRef.current = 0
    setIsFading(false)
    setFallbackStartPlayId(0)
  }, [open])

  useEffect(() => {
    if (!playbackOpen || !card || playId <= 0) return

    pendingCardRef.current = card
    startedPlayIdRef.current = playId
    startSentRef.current = false
    setFallbackStartPlayId(0)

    if (iframeReadyRef.current) {
      tryStartPlayback()
    } else {
      requestReady()
    }
  }, [card, playbackOpen, playId, requestReady, tryStartPlayback])

  useEffect(() => {
    if (usesWatchReveal || !playbackOpen || !card || playId <= 0) return
    if (
      fadedPlayIdRef.current === playId ||
      completedPlayIdRef.current === playId
    ) {
      return
    }

    const startedAt = Date.now()
    const id = window.setInterval(() => {
      if (
        fadedPlayIdRef.current === playId ||
        completedPlayIdRef.current === playId ||
        Date.now() - startedAt > START_RETRY_TIMEOUT_MS
      ) {
        window.clearInterval(id)
        return
      }

      if (!iframeReadyRef.current) {
        requestReady()
      }

      const payload = pendingCardRef.current
      if (!payload || startedPlayIdRef.current !== playId) return

      startSentRef.current = true
      postStart(payload, playId, mutedRef.current, skipCardReveal)
      if (!skipCardReveal || iframeReadyRef.current) {
        setFallbackStartPlayId((currentPlayId) =>
          currentPlayId === playId ? currentPlayId : playId,
        )
      }
    }, START_RETRY_INTERVAL_MS)

    return () => window.clearInterval(id)
  }, [
    card,
    playbackOpen,
    playId,
    postStart,
    requestReady,
    skipCardReveal,
    usesWatchReveal,
  ])

  useEffect(() => {
    if (
      !open ||
      !waitForMedia ||
      usesWatchReveal ||
      !card ||
      playId <= 0 ||
      iframeMediaReadyPlayId === playId
    )
      return
    const prepare = () => {
      requestReady()
      iframeRef.current?.contentWindow?.postMessage(
        { type: "GACHA_REVEAL_PRELOAD", playId, payload: card },
        "*",
      )
    }
    prepare()
    const interval = window.setInterval(prepare, START_RETRY_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [
    card,
    iframeMediaReadyPlayId,
    open,
    playId,
    requestReady,
    usesWatchReveal,
    waitForMedia,
  ])

  useEffect(() => {
    if (!shouldRender) return

    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      if (usesWatchReveal) return

      if (open && waitForMedia && event.data?.playId === playId) {
        if (event.data?.type === "GACHA_REVEAL_MEDIA_READY") {
          setIframeMediaReadyPlayId(playId)
          return
        }
        if (event.data?.type === "GACHA_REVEAL_MEDIA_ERROR") {
          mediaFailureRef.current()
          return
        }
      }

      if (event.data?.type === "GACHA_REVEAL_READY") {
        iframeReadyRef.current = true
        tryStartPlayback()
        return
      }

      if (event.data?.type === "GACHA_REVEAL_FADE") {
        if (!playbackOpen) return
        const messagePlayId = event.data.playId
        if (
          typeof messagePlayId === "number" &&
          messagePlayId > 0 &&
          messagePlayId !== playId
        ) {
          return
        }
        if (fadedPlayIdRef.current === playId) return

        triggerFade()
        return
      }

      if (event.data?.type === "GACHA_REVEAL_COMPLETE") {
        if (!playbackOpen) return
        const messagePlayId = event.data.playId
        if (
          typeof messagePlayId === "number" &&
          messagePlayId > 0 &&
          messagePlayId !== playId
        ) {
          return
        }
        if (completedPlayIdRef.current === playId) return

        completedPlayIdRef.current = playId
        triggerFade()
        onComplete()
      }
    }

    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [
    onComplete,
    open,
    playbackOpen,
    playId,
    shouldRender,
    waitForMedia,
    triggerFade,
    tryStartPlayback,
    usesWatchReveal,
  ])

  useEffect(() => {
    if (
      usesWatchReveal ||
      !playbackOpen ||
      !card ||
      playId <= 0 ||
      fallbackStartPlayId !== playId
    ) {
      return
    }

    const fadeMs = skipCardReveal
      ? MULTI_PACK_REVEAL_FADE_MS
      : SINGLE_PACK_REVEAL_FADE_MS
    const durationMs = skipCardReveal
      ? MULTI_PACK_REVEAL_MS
      : SINGLE_PACK_REVEAL_MS
    const fadeId = window.setTimeout(() => {
      triggerFade()
    }, fadeMs + REVEAL_FADE_GRACE_MS)
    const id = window.setTimeout(() => {
      if (completedPlayIdRef.current === playId) return

      completedPlayIdRef.current = playId
      triggerFade()
      onComplete()
    }, durationMs + REVEAL_COMPLETE_GRACE_MS)

    return () => {
      window.clearTimeout(fadeId)
      window.clearTimeout(id)
    }
  }, [
    card,
    fallbackStartPlayId,
    onComplete,
    playbackOpen,
    playId,
    skipCardReveal,
    triggerFade,
    usesWatchReveal,
  ])

  const handleIframeLoad = useCallback(() => {
    iframeReadyRef.current = false
    requestReady()
  }, [requestReady])

  if (!mounted || !shouldRender) return null

  const embedSrc =
    iframeSession !== null
      ? `${EMBED_PATH}?v=${EMBED_BUILD}&session=${iframeSession}`
      : null

  return createPortal(
    <div
      className={
        playbackOpen
          ? "fixed inset-0 z-[200] flex items-center justify-center bg-[#04030a]"
          : "pointer-events-none fixed top-0 left-0 h-px w-px overflow-hidden opacity-0"
      }
      role="presentation"
      aria-hidden={!playbackOpen}
    >
      {playbackOpen && !usesWatchReveal ? (
        <GachaRevealMuteButton
          muted={muted}
          onToggle={handleMuteToggle}
          className="absolute top-4 right-4 z-[210]"
        />
      ) : null}
      {usesWatchReveal ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-0 left-0 h-px w-px overflow-hidden opacity-0"
        >
          {watchRevealPreloadSources.map((src) => (
            <video
              key={src}
              data-watch-reveal-preload="true"
              muted
              playsInline
              preload="auto"
              src={src}
            />
          ))}
        </div>
      ) : null}
      {watchRevealPlan.kind === "watch-single" && playbackOpen && card ? (
        <div
          className={cn(
            "pointer-events-none h-full w-full transition-opacity ease-out",
            isFading ? "opacity-0" : "opacity-100",
          )}
          style={{ transitionDuration: `${GACHA_REVEAL_FADE_MS}ms` }}
        >
          <GachaWatchReveal
            cardImage={card.image}
            cardName={card.name}
            muted={muted}
            plan={watchRevealPlan}
            onFade={triggerFade}
            onComplete={onComplete}
          />
        </div>
      ) : watchRevealPlan.kind === "watch-multi" && playbackOpen ? (
        <div
          className={cn(
            "pointer-events-none h-full w-full transition-opacity ease-out",
            isFading ? "opacity-0" : "opacity-100",
          )}
          style={{ transitionDuration: `${GACHA_REVEAL_FADE_MS}ms` }}
        >
          <GachaWatchMultiReveal
            muted={muted}
            plan={watchRevealPlan}
            onFade={triggerFade}
            onComplete={onComplete}
          />
        </div>
      ) : !usesWatchReveal && embedSrc ? (
        <iframe
          key={iframeSession}
          ref={iframeRef}
          src={embedSrc}
          title="Gacha pack reveal"
          className={cn(
            "pointer-events-none h-full w-full border-0 transition-opacity ease-out",
            isFading ? "opacity-0" : "opacity-100",
          )}
          style={{ transitionDuration: `${GACHA_REVEAL_FADE_MS}ms` }}
          allow="autoplay"
          onLoad={handleIframeLoad}
        />
      ) : (
        <p className="text-sm text-white/50">Loading reveal...</p>
      )}
    </div>,
    document.body,
  )
}

export const GachaRevealOverlay = reatomComponent(
  GachaRevealOverlayBase,
  "GachaRevealOverlay",
)
